import { CountdownRenderProps } from 'react-countdown';
import styled, { css } from 'styled-components';
import { fontFamilies } from '~/theme/typography';

// Presentation shared by every countdown on the site. Deliberately holds no behaviour
// and no data source — each consumer supplies its own. It lived under a single
// feature's folder until a second consumer made the misfiling visible.

/**
 * `TimeNumber`'s `min-height` over `TimeLabel`'s. Exported so a caller that renders
 * the digits after hydration can reserve their height and avoid a layout shift,
 * without asserting the sum in a comment in another file.
 */
export const COUNTDOWN_DIGITS_MIN_HEIGHT = '2.25rem';

/**
 * Static above 1280px is the exception, not the rule: `breakpoints.lg` is a
 * `max-width` query, so the timer is `fixed` to the viewport top on desktop and in
 * normal flow on mobile and tablet.
 *
 * `padding-top: 0` in flow because the PAGE owns the header clearance —
 * `PageWrapper`'s `paddingTop` on the product grids, an explicit rule on the gate
 * screens. Shared rather than copied per caller: this block carries that inverted
 * breakpoint plus four declarations that are inert once `position: static`, and it was
 * duplicated verbatim between `/shop` and the private sale until it drifted.
 */
export const countdownPositioning = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 1.5rem;
  gap: 2px;
  position: fixed;
  /* Clears the rolling header's centred message when it is on (BPES-254). */
  top: var(--rolling-header-countdown-offset, 0);
  width: 100%;
  left: 0;
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.elevated};

  @media ${({ theme }) => theme.breakpoints.lg} {
    position: static;
    padding-top: 0;
    margin-bottom: 1rem;
  }

  @media ${({ theme }) => theme.breakpoints.md} {
    gap: 0;
  }
`;

export const CountdownTitle = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 100;
  line-height: normal;
  letter-spacing: 0.03125rem;
  text-transform: uppercase;

  @media ${({ theme }) => theme.breakpoints.sm} {
    font-size: 0.75rem;
  }
`;

export const countdownRenderer = ({
  formatted,
  completed,
}: CountdownRenderProps) => {
  if (completed) return null;

  const { days, hours, minutes, seconds } = formatted;

  return (
    <CountdownWrapper>
      <TimeBlock>
        <TimeNumber>{days}</TimeNumber>
        <TimeLabel>Days</TimeLabel>
      </TimeBlock>
      <TimeSeparator>:</TimeSeparator>
      <TimeBlock>
        <TimeNumber>{hours}</TimeNumber>
        <TimeLabel>Hrs</TimeLabel>
      </TimeBlock>
      <TimeSeparator>:</TimeSeparator>
      <TimeBlock>
        <TimeNumber>{minutes}</TimeNumber>
        <TimeLabel>Mins</TimeLabel>
      </TimeBlock>
      <TimeSeparator>:</TimeSeparator>
      <TimeBlock>
        <TimeNumber>{seconds}</TimeNumber>
        <TimeLabel>Secs</TimeLabel>
      </TimeBlock>
    </CountdownWrapper>
  );
};

const CountdownWrapper = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
`;

const TimeBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Exported alongside CountdownTitle so a page can restyle the digits for its own
// background without changing this shared presentation for every other surface.
export const TimeNumber = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 100;
  line-height: 1.5rem;
  letter-spacing: 0.03125rem;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
  min-height: 1.5rem;
  min-width: 1.5rem;
  text-align: center;
`;

export const TimeLabel = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 0.5rem;
  font-style: normal;
  font-weight: 100;
  line-height: 1.2;
  letter-spacing: 0.03125rem;
  text-transform: uppercase;
  min-height: 0.75rem;
`;

export const TimeSeparator = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-family: ${fontFamilies.PantonRustHeavy};
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 100;
  line-height: 1.5;
  letter-spacing: 0.03125rem;
  text-transform: uppercase;
  min-height: 1.5rem;
`;
